import AvatarM from "@common/components/avatar/AvatarM"
import { BadgeM } from "@common/components/badge/BadgeM"
import StatisticsList from "@common/components/statistics/StatisticsList"
import TextM from "@common/components/text/TextM"
import TitleM from "@common/components/text/TitleM"
import { HeaderContainerM } from "@common/header/HeaderContainerM"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { studentBriefService } from "@data/services/studentBriefService"
import { studentsService } from "@data/services/studentsService"
import { AbilityContext } from "@data/utility/Can"
import { capitalize } from "@data/utility/Utils"
import * as amplitude from "@amplitude/analytics-react-native"
import moment from "moment"
import React, { useContext, useEffect, useState } from "react"
import { ScrollView, View } from "react-native"
import { ActivityIndicator, IconButton } from "react-native-paper"
import { connect } from "react-redux"
import { hasSentTrialSessionPreparation, LeadScoreBadge } from "@common/components/badge/LeadScoreBadge"
import { ParentCallRequestedBadge } from "@common/components/badge/ParentCallRequestedBadge"
import { ProfileSection } from "../../student/account/ProfileSection"
import { SkillSetTitleButton } from "../../student/account/SkillSetTitleButton"
import { SubscriptionPrivilegeChip } from "../../student/account/SubscriptionPrivilegeChip"
import ProfileDetails, { profilItem } from "./ProfilDetails"
import StudentsListItem from "./StudentsListItem"
import { PrioritizedSchoolSubjects } from "./ProfilDetails"
import ObjectivesSummaryDisplay from "@common/account/ObjectivesSummaryDisplay"
import FilledButtonM from "@common/components/button/FilledButtonM"
import StudyProgramChapterSelectionModal from "@common/chapters/StudyProgramChapterSelectionModal"
import { resolveSessionObjective } from "@common/components/objectives/ObjectiveSubjectsBadges"
import {
    BriefProfileSection,
    BriefSessionSection,
    RegenerateButton,
} from "./StudentBriefSections"
import { StudentHealthSection } from "./StudentHealthSection"

// « Précisions sur le besoin » — free text carried by the student's demande de
// tutorat en vigueur (StudentResource#current_request_precisions). Shown
// read-only to the tutor right under the objectives; hidden when empty (the
// field is only present on tutor-facing payloads).
const RequestPrecisionsSection = ({ precisions }) =>
    precisions ? (
        <View
            testID="student-request-precisions"
            style={{ marginTop: 12, alignSelf: "stretch" }}
        >
            <TextM fontSize={13} fontWeight="Bold" color={theme.colors.grey}>
                Précisions sur le besoin
            </TextM>
            <TextM
                wrap
                fontSize={13}
                color={theme.colors.grey}
                style={{ marginTop: 2, flexShrink: 1 }}
            >
                {precisions}
            </TextM>
        </View>
    ) : null

const StudentProfil = (props) => {
    const { navigation, noHeader = false, onPressAdditionalActions = () => {} } = props

    const student = props.student
        ? props.student
        : props.students?.find(
              (x) => x?.id === parseInt(props.route.params?.studentId)
          ) ?? null
    const dimensions = useContext(DimensionsContext)

    const withActions = props.route?.params?.withActions ?? false
    const prevScreen = props.route?.params?.prevScreen ?? null

    // Brief props — from direct props or route params
    const objectiveId = props.objectiveId ?? props.route?.params?.objectiveId ?? null
    const liveOccurrenceId = props.liveOccurrenceId ?? props.route?.params?.liveOccurrenceId ?? null
    const briefContext = props.context ?? props.route?.params?.context ?? "profile"

    // Brief local state
    const [briefData, setBriefData] = useState(null)

    // Study program viewer (read-only) — shortcut from the profile.
    // The chapter of the day relevant to the session objective is highlighted.
    const [showProgramModal, setShowProgramModal] = useState(false)
    const sessionObjective = resolveSessionObjective(
        student,
        props.liveSessionId
    )

    // Fetch student data
    useEffect(() => {
        props.apiRequest(studentsService.getStudent, {
            id: props.route?.params?.studentId ?? student?.id,
        })
    }, [])

    // Fetch brief data
    useEffect(() => {
        const studentId = props.route?.params?.studentId ?? student?.id
        if (studentId) {
            const params = {}
            if (objectiveId) params.objective_id = objectiveId
            if (liveOccurrenceId) params.live_occurrence_id = liveOccurrenceId
            props.apiRequest(
                studentBriefService.getStudentBrief,
                { id: studentId },
                {},
                params
            )
            amplitude.track("student_brief.opened", {
                context: briefContext,
                student_id: studentId,
                has_objective: !!objectiveId,
            })
        }
    }, [])

    useApiStatus(studentsService.getStudent, null, true, () => {})

    useApiStatus(
        studentBriefService.getStudentBrief,
        null,
        true,
        (data) => setBriefData(data)
    )

    useApiStatus(
        studentBriefService.regenerateStudentBrief,
        null,
        true,
        (data) => setBriefData(data)
    )

    const handleRegenerate = () => {
        const body = objectiveId ? { objective_id: objectiveId } : {}
        props.apiRequest(
            studentBriefService.regenerateStudentBrief,
            { id: student?.id },
            body
        )
        amplitude.track("student_brief.regenerated", {
            context: briefContext,
            student_id: student?.id,
        })
    }

    const ability = useContext(AbilityContext)
    const isAdmin = ability?.can("read", "backOffice")

    // Health indicators (admin-only)
    const [healthData, setHealthData] = useState(null)

    useEffect(() => {
        const studentId = props.route?.params?.studentId ?? student?.id
        if (studentId && isAdmin) {
            props.apiRequest(studentBriefService.getStudentHealth, { id: studentId })
        }
    }, [isAdmin])

    useApiStatus(
        studentBriefService.getStudentHealth,
        null,
        false,
        (data) => setHealthData(data)
    )

    const profileBrief = briefData?.profile
    const sessionBrief = briefData?.session
    const profileData = profileBrief?.structured_data
    const sessionData = sessionBrief?.structured_data
    const isNew = profileData?.session_count != null && profileData.session_count <= 3
    const isBriefLoading = props.getStudentBriefRequest && !briefData

    // Durable special needs (read-only chips), surfaced next to the class so the
    // tutor sees them at a glance. Slug => label referential served by
    // static-data (backend StudentProfile::SPECIAL_NEEDS is the single source of
    // truth). The durable tutor gender preference is deliberately NOT displayed
    // here: it's attribution data, not session data.
    const specialNeedLabels = props.staticData?.student_special_needs ?? {}
    const specialNeeds = (student?.profile?.special_needs ?? []).filter(
        (slug) => specialNeedLabels[slug]
    )

    // Shortcut to the (read-only) study program viewer — only when the
    // student actually has one.
    const programShortcut = student?.has_study_program ? (
        <FilledButtonM
            onPress={() => setShowProgramModal(true)}
            mode="outlined"
            icon="book-open"
            label="Programme de révision"
            style={{ width: null, alignSelf: "flex-start", marginTop: 8 }}
            labelStyle={{ fontSize: 12 }}
        />
    ) : null

    return (
        <View
            style={{
                flex: 1,
                alignItems: "center",
                justifyContent: "flex-start",
                width: props.student ? dimensions.width : "100%",
                height: props.student ? dimensions.height : null,
                alignSelf: "center",
            }}
        >
            {!noHeader && (
                <HeaderContainerM>
                    <TitleM>Profil</TitleM>
                    <IconButton
                        icon={"times"}
                        iconColor={theme.colors.primary}
                        size={32}
                        style={{
                            position: "absolute",
                            left: 4,
                        }}
                        containerColor={theme.colors.transparent}
                        onPress={() => {
                            if (prevScreen) {
                                const prevScreenArray =
                                    prevScreen.split("/")
                                navigation.replace(prevScreenArray[0], {
                                    screen: prevScreenArray[1],
                                })
                            } else if (navigation.canGoBack()) {
                                navigation.goBack()
                            } else {
                                navigation.replace("menu")
                            }
                        }}
                    />
                </HeaderContainerM>
            )}
            {props.getStudentRequest ? (
                <ActivityIndicator
                    style={{
                        marginTop: 30,
                    }}
                    color={theme.colors.primary}
                />
            ) : (
                <ScrollView
                    style={{
                        width: "100%",
                    }}
                    contentContainerStyle={{
                        alignItems: "center",
                    }}
                >
                    <View
                        style={{
                            width: dimensions?.width,
                        }}
                    >
                        {/* 1. Student header — identity (full width) */}
                        <ProfileSection title={student?.display_name} style={{ paddingHorizontal: 30 }}>
                            <View style={{ flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                                {student?.profile?.class && (
                                    <TextM fontSize={13} color={theme.colors.grey}>{student.profile.class}</TextM>
                                )}
                                {profileData?.session_count != null && (
                                    <TextM fontSize={13} color={theme.colors.grey}>
                                        · {profileData.session_count} séance{profileData.session_count > 1 ? "s" : ""}
                                    </TextM>
                                )}
                                {student?.created_at && (
                                    <TextM fontSize={13} color={theme.colors.grey}>
                                        · Membre depuis {moment(student.created_at).format("MMM YYYY")}
                                    </TextM>
                                )}
                                {isNew && (
                                    <BadgeM
                                        customContent="Nouvel élève"
                                        style={{ backgroundColor: theme.colors.info, color: theme.colors.white }}
                                    />
                                )}
                            </View>
                            {specialNeeds.length > 0 && (
                                <View style={{ flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
                                    {specialNeeds.map((slug) => (
                                        <BadgeM
                                            key={slug}
                                            customContent={specialNeedLabels[slug]}
                                            style={{ backgroundColor: theme.colors.warning, color: theme.colors.white }}
                                        />
                                    ))}
                                </View>
                            )}
                            {(student?.subscription_plan_display || student?.subscription_duration_label) && (
                                <View style={{ flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
                                    {student?.subscription_plan_display && (
                                        <TextM fontSize={13} color={theme.colors.grey}>
                                            {student.subscription_plan_display}
                                        </TextM>
                                    )}
                                    {student?.subscription_plan_display && student?.subscription_duration_label && (
                                        <TextM fontSize={13} color={theme.colors.grey}>·</TextM>
                                    )}
                                    {student?.subscription_duration_label && (
                                        <TextM fontSize={13} color={theme.colors.grey}>
                                            {student.subscription_duration_label}
                                        </TextM>
                                    )}
                                </View>
                            )}
                            <AvatarM
                                user={student}
                                style={{
                                    position: "absolute",
                                    top: 15,
                                    right: 15,
                                }}
                                size={60}
                            />
                        </ProfileSection>

                        {/* 2+3. Two-column layout on wide screens: Objectifs | Brief séance */}
                        {dimensions?.width >= 700 && sessionData ? (
                            <View style={{ flexDirection: "row", width: dimensions?.width }}>
                                <View style={{ flex: 1 }}>
                                    <ProfileSection title={Array.isArray(student?.pedagogical_objectives) && student.pedagogical_objectives.length > 0 ? "Objectifs" : "Matières"} noBorder style={{ paddingHorizontal: 20, width: "100%" }}>
                                        {(() => {
                                            const isAdmin = ability.can("read", "backOffice")
                                            const hasObjectives = Array.isArray(student?.pedagogical_objectives) && student.pedagogical_objectives.length > 0

                                            if (isAdmin || hasObjectives) {
                                                return (
                                                    <ObjectivesSummaryDisplay
                                                        objectives={(student?.pedagogical_objectives || []).filter((o) => o?.status !== "closed")}
                                                        schoolSubjects={props.staticData?.school_subjects || []}
                                                        objectiveNeeds={props.staticData?.objective_needs || []}
                                                        currentLiveSessionId={props.liveSessionId}
                                                        compact={false}
                                                        availableLiveSessions={student?.live_sessions || []}
                                                        isAdmin={isAdmin}
                                                        studentId={student?.id}
                                                        user={student}
                                                    />
                                                )
                                            } else {
                                                return <PrioritizedSchoolSubjects student={student} />
                                            }
                                        })()}

                                        <RequestPrecisionsSection
                                            precisions={
                                                student?.current_request_precisions
                                            }
                                        />

                                        {programShortcut}

                                        {isBriefLoading ? (
                                            <ActivityIndicator
                                                style={{ marginTop: 16 }}
                                                size="small"
                                                color={theme.colors.primary}
                                            />
                                        ) : briefData ? (
                                            <BriefProfileSection
                                                profileBrief={profileBrief}
                                                profileData={profileData}
                                            />
                                        ) : null}
                                    </ProfileSection>
                                </View>
                                <View style={{ flex: 1 }}>
                                    <ProfileSection title={"Brief séance"} noBorder style={{ paddingHorizontal: 20, width: "100%" }}>
                                        <BriefSessionSection
                                            sessionBrief={sessionBrief}
                                            sessionData={sessionData}
                                            isMultiObjective={(student?.pedagogical_objectives || []).filter((o) => o?.status !== "closed").length > 1}
                                            subjectLabel={sessionData?.objective?.subjects?.length === 1 ? sessionData.objective.subjects[0].name : null}
                                        />
                                    </ProfileSection>
                                </View>
                            </View>
                        ) : (
                            <>
                                {/* Stacked layout: narrow screens or no session brief */}
                                {sessionData && (
                                    <ProfileSection title={"Brief séance"} style={{ paddingHorizontal: 30 }}>
                                        <BriefSessionSection
                                            sessionBrief={sessionBrief}
                                            sessionData={sessionData}
                                            isMultiObjective={(student?.pedagogical_objectives || []).filter((o) => o?.status !== "closed").length > 1}
                                            subjectLabel={sessionData?.objective?.subjects?.length === 1 ? sessionData.objective.subjects[0].name : null}
                                        />
                                    </ProfileSection>
                                )}

                                <ProfileSection title={Array.isArray(student?.pedagogical_objectives) && student.pedagogical_objectives.length > 0 ? "Objectifs" : "Matières"} style={{ paddingHorizontal: 30 }}>
                                    {(() => {
                                        const isAdmin = ability.can("read", "backOffice")
                                        const hasObjectives = Array.isArray(student?.pedagogical_objectives) && student.pedagogical_objectives.length > 0

                                        if (isAdmin || hasObjectives) {
                                            return (
                                                <ObjectivesSummaryDisplay
                                                    objectives={(student?.pedagogical_objectives || []).filter((o) => o?.status !== "closed")}
                                                    schoolSubjects={props.staticData?.school_subjects || []}
                                                    objectiveNeeds={props.staticData?.objective_needs || []}
                                                    currentLiveSessionId={props.liveSessionId}
                                                    compact={false}
                                                    availableLiveSessions={student?.live_sessions || []}
                                                    isAdmin={isAdmin}
                                                    studentId={student?.id}
                                                    user={student}
                                                />
                                            )
                                        } else {
                                            return <PrioritizedSchoolSubjects student={student} />
                                        }
                                    })()}

                                    <RequestPrecisionsSection
                                        precisions={
                                            student?.current_request_precisions
                                        }
                                    />

                                    {programShortcut}

                                    {isBriefLoading ? (
                                        <ActivityIndicator
                                            style={{ marginTop: 16 }}
                                            size="small"
                                            color={theme.colors.primary}
                                        />
                                    ) : briefData ? (
                                        <BriefProfileSection
                                            profileBrief={profileBrief}
                                            profileData={profileData}
                                        />
                                    ) : null}
                                </ProfileSection>
                            </>
                        )}

                        {/* 4. Actions */}
                        <ProfileSection style={{ paddingHorizontal: 30 }}>
                            <SubscriptionPrivilegeChip
                                product={capitalize(student?.current_offer)}
                                style={{ marginTop: 5 }}
                            />
                            {withActions && (
                                <StudentsListItem
                                    student={student}
                                    style={{ marginTop: 10 }}
                                    showLeftSection={false}
                                    chatButton={false}
                                    nav={navigation}
                                    onPressAdditionalActions={onPressAdditionalActions}
                                />
                            )}
                        </ProfileSection>

                        {isAdmin && (
                            <ProfileSection title={"Santé élève"} style={{ paddingHorizontal: 30 }}>
                                {!healthData ? (
                                    <ActivityIndicator
                                        size="small"
                                        color={theme.colors.primary}
                                        style={{ marginVertical: 10 }}
                                    />
                                ) : (
                                    <StudentHealthSection data={healthData} />
                                )}
                            </ProfileSection>
                        )}

                        <ProfileSection style={{ paddingHorizontal: 30 }}>
                            <SkillSetTitleButton
                                skillSet={student?.skill_sets?.find(
                                    (s) =>
                                        s?.id ===
                                        student?.title_skill_set_id
                                )}
                                style={{
                                    width: dimensions?.width - 20,
                                    marginTop: 10,
                                }}
                                withChampion={false}
                                readOnly={true}
                            />

                            <StatisticsList
                                student={student}
                                studentId={null}
                                navigation={navigation}
                                style={{ marginTop: 10 }}
                            />
                        </ProfileSection>

                        {isAdmin && (
                            <ProfileSection title={"Demande d'essai"} style={{ paddingHorizontal: 30 }}>
                                <View
                                    style={{
                                        width: "100%",
                                        flex: 1,
                                    }}
                                >
                                    {profilItem(
                                        "Essai confirmé",
                                        student?.trial_confirmed_notification
                                            ? "Oui"
                                            : "Non"
                                    )}
                                    {profilItem(
                                        "Précisions sur les besoins",
                                        student?.profile
                                            ?.parent_evaluation_comment ??
                                            "-"
                                    )}
                                    {profilItem(
                                        "Souhaite être rappelé",
                                        student?.profile
                                            ?.parent_call_requested ===
                                            null ? (
                                            "-"
                                        ) : (
                                            <ParentCallRequestedBadge
                                                user={student}
                                            />
                                        )
                                    )}
                                    {profilItem(
                                        "Travail à faire transmis",
                                        hasSentTrialSessionPreparation(student)
                                            ? "Oui"
                                            : "Non"
                                    )}
                                    {profilItem(
                                        "Lead score",
                                        <LeadScoreBadge user={student} />
                                    )}
                                </View>
                            </ProfileSection>
                        )}

                        <ProfileSection title={"Informations"} style={{ paddingHorizontal: 30 }}>
                            <ProfileDetails student={student} />
                        </ProfileSection>

                    </View>
                </ScrollView>
            )}
            {showProgramModal && (
                <StudyProgramChapterSelectionModal
                    visible={showProgramModal}
                    onDismiss={() => setShowProgramModal(false)}
                    studentId={student?.id}
                    readOnly
                    title="Programme de révision"
                    highlightSubjectIds={sessionObjective?.subject_ids ?? []}
                />
            )}
        </View>
    )
}

const mapStateToProps = (state) => {
    return {
        getUserRequest: state.api.getUser?.request,
        getUserSuccess: state.api.getUser?.success,
        getStudentRequest: state.api.getStudent?.request,
        getStudentSuccess: state.api.getStudent?.success,
        getStudentBriefRequest: state.api.getStudentBrief?.request,
        regenerateStudentBriefRequest: state.api.regenerateStudentBrief?.request,
        getStudentHealthRequest: state.api.getStudentHealth?.request,
        successes: state.data.staticData?.successes,
        students: state.data.currentUser?.object?.students,
        staticData: state.data.staticData,
        liveSessionId: state.liveMode?.liveSessionId,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
}

export default connect(mapStateToProps, mapDispatchToProps)(StudentProfil)
